import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Property } from 'shared-types/Property';
import { SpaEntityType } from 'shared-types/Spa';
import type { ExtrasGuest, ExtrasItem } from 'voyager';
import { ExtrasTemplate } from 'voyager';

import { Spinner } from '@common/components/Spinner';
import { adaptSpaExtras, getExtraFieldKey, getExtraId } from '@features/spa/utils/adaptExtras';
import { useExtrasLabels } from '@features/spa/hooks';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';

import { SpaTreatmentFormValues } from './SpaTreatmentBookingV2.types';
import { useSubmitTreatment } from './hooks/useSubmitTreatment';

type SpaTreatmentExtrasV2Props = {
  spaEntity: SpaEntityType;
  property: Property;
  language: string;
  id: number;
  onClose: (showMiniBasket: boolean) => void;
  onBack: () => void;
};

export const SpaTreatmentExtrasV2: FC<SpaTreatmentExtrasV2Props> = ({
  spaEntity,
  property,
  language,
  id,
  onClose,
  onBack,
}) => {
  const { t } = useTranslations();
  const extrasLabels = useExtrasLabels();
  const form = useFormContext<SpaTreatmentFormValues>();
  const watchedGuests = useWatch({ control: form.control, name: 'guests' });
  const watchedExtras = useWatch({ control: form.control, name: 'extras' });
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  );

  const { handleSubmit } = useSubmitTreatment({
    form,
    spaEntity,
    propertyId: property.id,
    language,
    id,
    onClose,
    hasExtras: true,
  });

  const adaptedExtras = useMemo(() => adaptSpaExtras(spaEntity), [spaEntity]);

  const selectedGuests = useMemo(
    () =>
      (watchedGuests || []).map(({ title, firstName, lastName, gender }) => ({
        title,
        firstName,
        lastName,
        gender,
      })),
    [watchedGuests]
  );

  const guests: ExtrasGuest[] = useMemo(
    () =>
      selectedGuests.map((g, i) => ({
        id: String(i),
        label: g.firstName && g.lastName ? `${g.firstName} ${g.lastName}` : `Guest ${i + 1}`,
      })),
    [selectedGuests]
  );

  const extrasItems: ExtrasItem[] = useMemo(
    () =>
      adaptedExtras.map((opt) => {
        const fieldKey = getExtraFieldKey(opt);
        const currentEntries = (watchedExtras as any)?.[fieldKey] || [];
        const selectedGuestIds = currentEntries.map((entry: any) =>
          String(selectedGuests.indexOf(entry.value))
        );
        return {
          id: String(getExtraId(opt)),
          title: opt.name,
          image: { src: opt.image?.url, alt: opt.name },
          price: opt.price || { amount: 0, currencyCode: spaEntity.price?.currencyCode || 'GBP' },
          selected: currentEntries.length > 0,
          quantity: currentEntries.length,
          selectedGuestIds: selectedGuestIds.filter((gId: string) => gId !== '-1'),
        };
      }),
    [adaptedExtras, watchedExtras, selectedGuests, spaEntity]
  );

  const handleAdd = useCallback(
    (extraId: string) => {
      const opt = adaptedExtras.find((o) => String(getExtraId(o)) === extraId);
      if (!opt) return;
      const fieldKey = getExtraFieldKey(opt);
      const currentExtras = form.getValues('extras') || {};

      if (selectedGuests.length === 1) {
        const guest = selectedGuests[0];
        const entries = [
          {
            label: `${guest.firstName} ${guest.lastName}`,
            value: guest,
            price: opt.price,
            id: extraId,
          },
        ];
        form.setValue('extras', { ...currentExtras, [fieldKey]: entries });
      } else {
        const entries = selectedGuests.map((guest) => ({
          label: `${guest.firstName} ${guest.lastName}`,
          value: guest,
          price: opt.price,
          id: extraId,
        }));
        form.setValue('extras', { ...currentExtras, [fieldKey]: entries });
      }
    },
    [adaptedExtras, form, selectedGuests]
  );

  const handleRemove = useCallback(
    (extraId: string) => {
      const opt = adaptedExtras.find((o) => String(getExtraId(o)) === extraId);
      if (!opt) return;
      const fieldKey = getExtraFieldKey(opt);
      const currentExtras = form.getValues('extras') || {};
      const { [fieldKey]: _, ...rest } = currentExtras;
      form.setValue('extras', rest);
    },
    [adaptedExtras, form]
  );

  const handleConfirmGuestSelection = useCallback(
    (extraId: string, guestIds: string[]) => {
      const opt = adaptedExtras.find((o) => String(getExtraId(o)) === extraId);
      if (!opt) return;
      const fieldKey = getExtraFieldKey(opt);
      const currentExtras = form.getValues('extras') || {};
      const entries = guestIds.map((gId) => {
        const guest = selectedGuests[Number(gId)];
        return {
          label: guest ? `${guest.firstName} ${guest.lastName}` : `Guest ${Number(gId) + 1}`,
          value: guest,
          price: opt.price,
          id: extraId,
        };
      });
      form.setValue('extras', { ...currentExtras, [fieldKey]: entries });
    },
    [adaptedExtras, form, selectedGuests]
  );

  const basePrice = spaEntity.price?.amount || 0;
  const discountedPrice = spaEntity.discount?.discountedPrice?.amount;
  const currencyCode = spaEntity.price?.currencyCode || 'GBP';
  const guestCount = selectedGuests.length || 1;

  const extrasTotal = useMemo(() => {
    if (!watchedExtras) return 0;
    return Object.values(watchedExtras as Record<string, any[]>).reduce((sum, entries) => {
      if (!Array.isArray(entries)) return sum;
      return sum + entries.reduce((s, e) => s + (e.price?.amount || 0), 0);
    }, 0);
  }, [watchedExtras]);

  const totalAmount =
    discountedPrice != null
      ? discountedPrice * guestCount + extrasTotal
      : basePrice * guestCount + extrasTotal;
  const originalAmount = discountedPrice != null ? basePrice * guestCount + extrasTotal : undefined;

  return (
    <>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <ExtrasTemplate
        items={extrasItems}
        guests={guests.length > 1 ? guests : undefined}
        onAdd={handleAdd}
        onRemove={handleRemove}
        onConfirmGuestSelection={handleConfirmGuestSelection}
        currencyCode={currencyCode}
        totalAmount={totalAmount}
        originalAmount={originalAmount}
        onCtaClick={handleSubmit}
        onBack={onBack}
        labels={{
          title: spaEntity.name,
          ctaLabel: t('spa.book'),
          footerTitle: t('spa.extras.footerTitle'),
          ...extrasLabels,
        }}
      />
    </>
  );
};
